import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Droplets, Car, Sparkles, Shield, Clock, MapPin, Award, Users, CheckCircle, Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AnimatedWave, WaterDroplets, WaveDivider } from '../components/WaveDivider';
import { FullPageWave } from '../components/FullPageWave';
import { useLanguage } from '../context/LanguageContext';

const EVERWASH_URL = 'https://app.everwash.com/#membership-plans/143';
const EXTERIOR_IMG = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/eif4mi42_474949816_1065542595376121_1473048260461701712_n.jpg';
const HAND_WASH_IMG = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/swslw90w_480790144_1087613703169010_4496990203856420121_n.jpg';
const SPRAY_WASH_IMG = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/tf446cm4_480579292_1087604693169911_8215014255740035868_n.jpg';
const TEAM_IMG = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/m5g831cy_847e99a5-cf46-4a04-ad90-1f557f3e898a.jpeg';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
};

const staggerChildren = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { staggerChildren: 0.1 }
};

const GOOGLE_REVIEWS = [
  {
    name: 'Amber Davis',
    badge: 'Local Guide',
    time: 'a year ago',
    rating: 5,
    text: 'We pay for the unlimited monthly rate. The people who do the service of hand washing before are polite and professional. We have not had any problems going through the automatic wash at all. Well worth the price and service. Tips are always appreciated for the fine workers.'
  },
  {
    name: 'Charli Barnoskie',
    badge: 'Local Guide',
    time: '4 years ago',
    rating: 5,
    text: 'These guys are always nice. The monthly membership is worth it. You can get a car wash every day! The vacuums here have good suction and are only a quarter at a time which is nice. There are plenty of car wash bays and vacuums.'
  },
  {
    name: 'Chris Corley',
    badge: 'Local Guide',
    time: 'a month ago',
    rating: 5,
    text: 'Great price. They are great. They hand wash the car before it goes through the automatic car wash. Always shiny.'
  },
  {
    name: 'Edna Kimble',
    badge: 'Local Guide',
    time: '7 months ago',
    rating: 5,
    text: 'EXCELLENT!! Prompt and professional service! My car looks AMAZING.'
  },
  {
    name: 'Kenny',
    badge: 'Local Guide',
    time: '2 years ago',
    rating: 5,
    text: 'They have all you need there, got water, great vacuums, car scents, everything you need. And always someone there to help if needed. Great place to wash your vehicle anytime.'
  },
  {
    name: 'Janice H',
    badge: 'Local Guide',
    time: '5 years ago',
    rating: 5,
    text: 'I have 3 messy boys, I love using the trash cans and vacuum to help fight the mess. I visit twice a week!! Great location, easy to use, up kept well.'
  },
  {
    name: 'Joseph Kavanagh',
    badge: 'Local Guide',
    time: '4 years ago',
    rating: 5,
    text: 'They always do a fabulous job of preparing your car for its wash and the wash results are amazing.'
  }
];

const GOOGLE_REVIEW_URL = 'https://maps.app.goo.gl/pzyefhoDAQCvaCp89';

const ReviewsSection = () => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const { t } = useLanguage();
  const visibleCount = typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 3;

  const next = () => {
    setCurrentIdx(i => (i + 1) % GOOGLE_REVIEWS.length);
    if (window.gtag) window.gtag('event', 'review_carousel', { event_category: 'engagement', event_label: 'next' });
  };
  const prev = () => {
    setCurrentIdx(i => (i - 1 + GOOGLE_REVIEWS.length) % GOOGLE_REVIEWS.length);
    if (window.gtag) window.gtag('event', 'review_carousel', { event_category: 'engagement', event_label: 'prev' });
  };

  const getVisible = () => {
    const items = [];
    for (let i = 0; i < visibleCount; i++) {
      items.push(GOOGLE_REVIEWS[(currentIdx + i) % GOOGLE_REVIEWS.length]);
    }
    return items;
  };

  return (
    <section className="bg-[#0F172A] py-20 md:py-32 relative overflow-hidden" data-testid="reviews-section">
      <WaveDivider variant="top" color="dark" />

      {/* Subtle decorative glow */}
      <div className="absolute top-20 left-1/4 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-1/4 w-60 h-60 bg-red-500/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <motion.div {...fadeInUp} className="text-center mb-16">
          <span className="text-red-500 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
            {t('reviews.badge')}
          </span>
          <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
            {t('reviews.title')}
          </h2>
          <div className="flex items-center justify-center gap-2 mb-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <p className="text-blue-200/70 text-sm">{t('reviews.google')}</p>
        </motion.div>

        {/* Review Cards */}
        <div className="relative">
          <div className="grid md:grid-cols-3 gap-6">
            {getVisible().map((review, idx) => (
              <motion.div
                key={`${review.name}-${currentIdx}-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors"
                data-testid={`review-card-${idx}`}
              >
                <Quote className="w-8 h-8 text-red-500/40 mb-4" />
                <p className="text-slate-300 text-sm leading-relaxed mb-6 min-h-[80px]">
                  "{review.text}"
                </p>
                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{review.name}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          ))}
                        </div>
                        <span className="text-slate-500 text-xs">{review.time}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={prev}
              className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
              data-testid="reviews-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-1.5">
              {GOOGLE_REVIEWS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIdx(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${idx === currentIdx ? 'bg-red-500 w-6' : 'bg-white/30 hover:bg-white/50'}`}
                  data-testid={`reviews-dot-${idx}`}
                />
              ))}
            </div>
            <button
              onClick={next}
              className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
              data-testid="reviews-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Leave a Review CTA */}
        <motion.div {...fadeInUp} className="text-center mt-12">
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="leave-review-btn"
          >
            <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold px-8 py-3 rounded-full">
              <Star className="w-4 h-4 mr-2 fill-yellow-400 text-yellow-400" />
              Leave Us a Review on Google
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export const HomePage = () => {
  const { t } = useLanguage();
  return (
    <div className="overflow-hidden">
      {/* Hero Section - Completely Redesigned */}
      <section className="relative min-h-screen lg:min-h-[90vh] flex items-center overflow-hidden py-20 lg:py-0" data-testid="hero-section">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF]">
          {/* Animated mesh gradient overlay */}
          <motion.div 
            className="absolute inset-0 opacity-30"
            style={{
              background: 'radial-gradient(circle at 20% 80%, #DC2626 0%, transparent 50%), radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)'
            }}
            animate={{
              background: [
                'radial-gradient(circle at 20% 80%, #DC2626 0%, transparent 50%), radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)',
                'radial-gradient(circle at 40% 20%, #DC2626 0%, transparent 50%), radial-gradient(circle at 60% 80%, #3B82F6 0%, transparent 50%)',
                'radial-gradient(circle at 80% 50%, #DC2626 0%, transparent 50%), radial-gradient(circle at 20% 50%, #3B82F6 0%, transparent 50%)',
                'radial-gradient(circle at 20% 80%, #DC2626 0%, transparent 50%), radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)'
              ]
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        
        {/* Water droplets animation */}
        <WaterDroplets count={25} />
        
        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8 md:py-12 relative z-10 w-full">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left: Content - takes 7 columns */}
            <div className="lg:col-span-7">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6"
              >
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-white/90 text-sm font-medium">{t('hero.badge')} • {t('hero.hours')}</span>
              </motion.div>
              
              {/* Main headline with staggered animation */}
              <div className="overflow-hidden mb-4 md:mb-6">
                <motion.h1 
                  className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter uppercase leading-[0.9]"
                  initial={{ y: 100 }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                  <span className="text-white block">{t('hero.line1')}</span>
                  <motion.span 
                    className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-400 block"
                    animate={{ 
                      backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
                    }}
                    transition={{ duration: 5, repeat: Infinity }}
                    style={{ backgroundSize: '200% 200%' }}
                  >
                    {t('hero.line2')}
                  </motion.span>
                  <span className="text-white block">{t('hero.line3')}</span>
                </motion.h1>
              </div>
              
              {/* Subheadline */}
              <motion.p 
                className="text-base md:text-lg text-blue-100/90 mb-6 max-w-lg leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                {t('hero.sub')}{' '}
                <span className="text-red-400 font-bold">$20/month</span>{t('hero.sub2')}{' '}
                <span className="text-white font-semibold">{t('hero.sub3')}</span>{' '}
                {t('hero.sub4')}
              </motion.p>
              
              {/* CTA Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row gap-3 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-everwash-cta">
                  <Button className="w-full sm:w-auto bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold text-base px-6 py-5 rounded-full shadow-lg shadow-red-500/30 hover:shadow-red-500/50 transition-all hover:-translate-y-1">
                    <Sparkles className="w-5 h-5 mr-2" />
                    {t('hero.cta1')}
                  </Button>
                </a>
                <a 
                  href="https://maps.app.goo.gl/pzyefhoDAQCvaCp89"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-directions-btn"
                >
                  <Button variant="outline" className="w-full sm:w-auto border-2 border-white/30 text-white hover:bg-white/10 backdrop-blur-sm font-bold text-base px-6 py-5 rounded-full transition-all hover:-translate-y-1">
                    <MapPin className="w-5 h-5 mr-2" />
                    {t('hero.cta2')}
                  </Button>
                </a>
              </motion.div>
              
              {/* Stats row */}
              <motion.div 
                className="flex flex-wrap gap-6 relative z-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                {[
                  { value: t('hero.stat1.value'), label: t('hero.stat1.label') },
                  { value: t('hero.stat2.value'), label: t('hero.stat2.label') },
                  { value: '$20/mo', label: t('hero.stat3.label') }
                ].map((stat, idx) => (
                  <div key={idx} className="text-center bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                    <div className="text-xl md:text-2xl font-black text-white">{stat.value}</div>
                    <div className="text-xs text-blue-200/70">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Right: Images - takes 5 columns */}
            <motion.div 
              className="lg:col-span-5 relative hidden lg:block"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              {/* Floating images composition */}
              <div className="relative h-[450px] xl:h-[500px]">
                {/* Main large image */}
                <motion.div
                  className="absolute top-0 right-0 w-56 xl:w-64 h-64 xl:h-72 rounded-3xl overflow-hidden shadow-2xl"
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                >
                  <img 
                    src={EXTERIOR_IMG} 
                    alt="Super Spray Car Wash"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/50 to-transparent" />
                </motion.div>
                
                {/* Secondary image */}
                <motion.div
                  className="absolute top-24 left-0 w-44 xl:w-52 h-56 xl:h-64 rounded-3xl overflow-hidden shadow-2xl border-4 border-white/20"
                  animate={{ y: [0, 20, 0] }}
                  transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                >
                  <img 
                    src={SPRAY_WASH_IMG} 
                    alt="Car wash in action"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                
                {/* Third floating image */}
                <motion.div
                  className="absolute bottom-0 right-8 w-36 xl:w-44 h-36 xl:h-44 rounded-2xl overflow-hidden shadow-2xl border-4 border-red-500"
                  animate={{ y: [0, -10, 0], rotate: [0, 2, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                  <img 
                    src={HAND_WASH_IMG} 
                    alt="Hand wash service"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                
                {/* Decorative elements */}
                <motion.div 
                  className="absolute -top-4 left-20 w-16 h-16 bg-red-500 rounded-full opacity-60 blur-xl"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.6, 0.8, 0.6] }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
                <motion.div 
                  className="absolute bottom-20 -right-4 w-24 h-24 bg-blue-400 rounded-full opacity-40 blur-2xl"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.6, 0.4] }}
                  transition={{ duration: 5, repeat: Infinity, delay: 1 }}
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Animated wave at bottom */}
        <AnimatedWave color="#FFFFFF" />
      </section>

      {/* Membership Section */}
      <section className="bg-white py-20 md:py-32" data-testid="membership-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              {t('membership.badge')}
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0F172A] mb-6">
              {t('membership.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('membership.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              { icon: Droplets, title: 'Unlimited Washes', desc: 'Wash your car as many times as you need, every single month.' },
              { icon: Shield, title: 'No Contracts', desc: 'Month-to-month membership. Cancel anytime, no questions asked.' },
              { icon: Sparkles, title: 'Premium Quality', desc: 'Sonny\'s equipment with Ryko SoftGloss for a perfect clean every time.' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: idx * 0.1 }}
                className="text-center p-8 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="w-16 h-16 bg-[#1E3A8A] rounded-xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="bg-[#1E3A8A] rounded-2xl p-8 md:p-12 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('membership.starting')} <span className="text-red-500">$20</span>{t('membership.permonth')}
            </h3>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto">
              That's less than two single washes! Join thousands of happy members who save money and keep their cars sparkling clean.
            </p>
            <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer" data-testid="membership-cta">
              <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold text-lg px-10 py-6 rounded-md btn-primary">
                {t('membership.cta')}
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* The Math Section - Highlighted */}
      <section className="bg-slate-50 py-20 md:py-28" data-testid="math-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              {t('math.badge')}
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0F172A] mb-10">
              {t('math.title')}
            </h2>
            
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-slate-100">
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                <div className="text-center p-6 bg-slate-50 rounded-xl">
                  <p className="text-slate-500 mb-2 text-sm font-medium">{t('math.single')}</p>
                  <p className="text-4xl font-black text-[#0F172A]">$10-16</p>
                  <p className="text-sm text-slate-400 mt-2">{t('math.pervisit')}</p>
                </div>
                <div className="text-center p-6 bg-[#1E3A8A] rounded-xl relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#DC2626] text-white text-xs font-bold px-3 py-1 rounded-full">{t('math.best')}</div>
                  <p className="text-blue-200 mb-2 text-sm font-medium">{t('math.everwash')}</p>
                  <p className="text-4xl font-black text-white">$20</p>
                  <p className="text-sm text-blue-200 mt-2">{t('math.unlimited')}</p>
                </div>
                <div className="text-center p-6 bg-green-50 rounded-xl">
                  <p className="text-green-600 mb-2 text-sm font-medium">You Save</p>
                  <p className="text-4xl font-black text-green-600">$50+</p>
                  <p className="text-sm text-green-500 mt-2">Per month (4+ washes)</p>
                </div>
              </div>
              
              <p className="text-lg text-slate-600 mb-8">
                {t('math.desc1')} <span className="font-bold text-[#0F172A]">{t('math.desc2')}</span> {t('math.desc3')} <span className="font-bold text-[#0F172A]">{t('math.desc4')}</span> {t('math.desc5')} <span className="font-bold text-[#DC2626]">{t('math.desc6')}</span> {t('math.desc7')}
              </p>

              <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer" data-testid="math-section-cta">
                <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold text-lg px-10 py-6 rounded-full btn-primary shadow-lg shadow-red-500/20">
                  {t('math.cta')} <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="bg-[#0F172A] py-20 md:py-32 relative" data-testid="about-section">
        <WaveDivider variant="top" color="dark" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeInUp}>
              <span className="text-red-500 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
                Our Story
              </span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
                The Original Tunnel Wash
              </h2>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                Super Spray Car Wash II has been keeping Tahlequah's vehicles clean since day one. As the original tunnel car wash in town, we've built our reputation on quality, convenience, and customer service.
              </p>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                We're proud to be the only full-service car wash in Tahlequah that combines hand washing with our automated tunnel - you get personal attention first, then we send you through for a perfect finish.
              </p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                {[
                  { icon: Award, label: 'Original Tunnel Wash' },
                  { icon: Users, label: 'Full-Service Team' },
                  { icon: Car, label: 'Hand & Automated' },
                  { icon: MapPin, label: 'Local Owned' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-white font-medium">{item.label}</span>
                  </div>
                ))}
              </div>

              <Link to="/about" data-testid="about-learn-more-btn">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 font-bold px-8 py-3 rounded-md">
                  Learn More About Us
                </Button>
              </Link>
            </motion.div>

            <motion.div 
              {...fadeInUp}
              className="relative"
            >
              <div className="relative">
                <img 
                  src={TEAM_IMG} 
                  alt="Super Spray Team"
                  className="w-full h-[500px] object-cover rounded-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 to-transparent rounded-2xl" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-white text-lg font-medium">Serving Tahlequah & surrounding communities</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="bg-white py-20 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              {t('services.badge')}
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0F172A] mb-6">
              {t('services.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From quick automated washes to full-service detailing, we have everything your vehicle needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                img: HAND_WASH_IMG,
                title: 'Full-Service Experience',
                desc: 'The best of both worlds: hand wash attention followed by our automated tunnel.',
                features: ['Hand prep & scrub first', 'Then through the tunnel', 'Complete thorough clean']
              },
              {
                img: SPRAY_WASH_IMG,
                title: 'Premium Tunnel Wash',
                desc: 'State-of-the-art Sonny\'s equipment with Ryko SoftGloss technology.',
                features: ['Soft-touch brushes', 'Triple foam polish', 'Spot-free rinse']
              },
              {
                img: EXTERIOR_IMG,
                title: 'Self-Service Bays',
                desc: 'DIY with professional-grade equipment at your pace.',
                features: ['High-pressure wand', 'Foam brush', 'Quarter vacuums ($0.25)']
              }
            ].map((service, idx) => (
              <motion.div
                key={idx}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: idx * 0.1 }}
                className="group service-card bg-white border border-slate-200 rounded-2xl overflow-hidden"
              >
                <div className="h-48 overflow-hidden img-zoom">
                  <img 
                    src={service.img} 
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">{service.title}</h3>
                  <p className="text-slate-600 mb-4">{service.desc}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-500">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-12">
            <Link to="/services" data-testid="services-view-all-btn">
              <Button className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white font-bold px-8 py-3 rounded-md btn-secondary">
                View All Services
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-slate-50 py-20 md:py-32" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              Why Super Spray
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0F172A] mb-6">
              Why Choose Us
            </h2>
          </motion.div>

          <motion.div {...staggerChildren} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Only Full-Service in Tahlequah', desc: 'The only car wash combining hand wash prep with an automated tunnel finish.' },
              { title: 'Original Tunnel Wash', desc: 'We were the first tunnel car wash in Tahlequah - proven quality since day one.' },
              { title: 'Unlimited Membership', desc: 'Wash as often as you need with EverWash for one low monthly price.' },
              { title: 'Premium Equipment', desc: 'Sonny\'s systems with Ryko SoftGloss for a safe, effective clean.' },
              { title: 'Convenient Location', desc: 'Easy access on S Muskogee Ave with plenty of space for all vehicles.' },
              { title: 'Open 7 Days', desc: 'Come by any day from 9 AM to 7 PM - we\'re here when you need us.' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                variants={{
                  initial: { opacity: 0, y: 20 },
                  whileInView: { opacity: 1, y: 0 }
                }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Customer Reviews */}
      <ReviewsSection />

      {/* FAQ Section */}
      <section className="bg-white py-20 md:py-32" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              {t('faq.badge')}
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0F172A] mb-6">
              {t('faq.title')}
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              { q: t('faq.q1'), a: t('faq.a1') },
              { q: t('faq.q2'), a: t('faq.a2') },
              { q: t('faq.q3'), a: t('faq.a3') },
              { q: t('faq.q4'), a: t('faq.a4') },
              { q: t('faq.q5'), a: t('faq.a5') },
              { q: t('faq.q6'), a: t('faq.a6') }
            ].map((faq, idx) => (
              <motion.details
                key={idx}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: idx * 0.05 }}
                className="group bg-slate-50 rounded-xl overflow-hidden"
                data-testid={`faq-item-${idx}`}
                onToggle={(e) => {
                  if (e.target.open && window.gtag) {
                    window.gtag('event', 'faq_open', {
                      event_category: 'engagement',
                      event_label: faq.q
                    });
                  }
                }}
              >
                <summary className="flex items-center justify-between cursor-pointer p-6 font-bold text-[#0F172A] hover:text-[#1E3A8A] transition-colors list-none [&::-webkit-details-marker]:hidden">
                  <span className="pr-4">{faq.q}</span>
                  <span className="flex-shrink-0 w-8 h-8 bg-[#1E3A8A] rounded-full flex items-center justify-center text-white text-lg group-open:rotate-45 transition-transform duration-300">+</span>
                </summary>
                <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                  {faq.a}
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* Full Page Wave Animation */}
      <FullPageWave />

      {/* Hours & Location */}
      <section className="bg-white py-20 md:py-32" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
                {t('location.badge')}
              </span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0F172A] mb-6">
                {t('location.title')}
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0F172A] mb-1">{t('location.hours')}</h3>
                    <p className="text-slate-600">{t('location.hoursval')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0F172A] mb-1">{t('location.address')}</h3>
                    <p className="text-slate-600">1581 S Muskogee Ave<br />Tahlequah, OK 74464</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://maps.app.goo.gl/pzyefhoDAQCvaCp89"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="location-directions-btn"
                >
                  <Button className="w-full sm:w-auto bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white font-bold px-8 py-3 rounded-md btn-secondary">
                    {t('location.directions')}
                  </Button>
                </a>
                <a href="tel:9182079463" data-testid="location-call-btn">
                  <Button variant="outline" className="w-full sm:w-auto border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white font-bold px-8 py-3 rounded-md">
                    Call Us
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="h-80 md:h-[400px] rounded-2xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3234.5!2d-94.97!3d35.91!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87c91c2d5a4a0001%3A0x1234567890abcdef!2s1581%20S%20Muskogee%20Ave%2C%20Tahlequah%2C%20OK%2074464!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Super Spray Car Wash II Location"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#DC2626] py-20 md:py-24 relative overflow-hidden" data-testid="final-cta-section">
        {/* Animated background pattern */}
        <motion.div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
          animate={{ x: [0, 60], y: [0, 60] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-6">
              Ready for a Cleaner Ride?
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Join EverWash today and get unlimited washes for one low monthly price. Your car deserves it.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer" data-testid="final-everwash-cta">
                <Button className="w-full sm:w-auto bg-white text-[#DC2626] hover:bg-slate-100 font-bold text-lg px-10 py-6 rounded-full shadow-lg">
                  Join EverWash Now
                </Button>
              </a>
              <Link to="/contact" data-testid="final-contact-btn">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 font-bold text-lg px-10 py-6 rounded-full">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
